export type ObservableMaterialProperty =
  | 'rigid'
  | 'flexible'
  | 'absorbent'
  | 'water-resistant'
  | 'rough'
  | 'smooth'
  | 'transparent'
  | 'opaque'
  | 'strong'
  | 'weak';

export interface MaterialsInvestigationMaterial {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly properties: readonly ObservableMaterialProperty[];
}

export interface MaterialTestObservationDefinition {
  readonly evidenceId: string;
  readonly propertyId: ObservableMaterialProperty;
  readonly observation: string;
}

export interface MaterialsPropertyTest {
  readonly id: string;
  readonly label: string;
  readonly modelDescription: string;
  readonly propertyOptions: readonly ObservableMaterialProperty[];
  readonly observations: Readonly<Record<string, MaterialTestObservationDefinition>>;
}

export interface MaterialsInvestigationTask {
  readonly purpose: string;
  readonly problem: string;
  readonly requiredProperties: readonly ObservableMaterialProperty[];
  readonly requiredObservationKeys: readonly string[];
  readonly buildResult: string;
}

export interface MaterialsChoiceFeedback {
  readonly materialId: string;
  readonly message: string;
}

export interface MaterialsInvestigationConfig {
  readonly id: string;
  readonly masteryKey: string;
  readonly title: string;
  readonly task: MaterialsInvestigationTask;
  readonly materials: readonly MaterialsInvestigationMaterial[];
  readonly tests: readonly MaterialsPropertyTest[];
  readonly minimumMaterialsInspected: number;
  readonly predictionPrompt: string;
  readonly comparisonPrompt: string;
  readonly choicePrompt: string;
  readonly bestMaterialId: string;
  readonly choiceFeedback: readonly MaterialsChoiceFeedback[];
  readonly successMessage: string;
  readonly conclusionId: string;
  readonly completionWorldEvent: string;
  readonly hints: readonly string[];
}

export function materialTestKey(materialId: string, testId: string) {
  return `${materialId}:${testId}`;
}

function findDuplicates(values: readonly string[]) {
  return values.filter((value, index) => values.indexOf(value) !== index);
}

/** Reject malformed science content before a production-art view binds it. */
export function validateMaterialsInvestigationConfig(config: MaterialsInvestigationConfig) {
  const materialIds = config.materials.map(material => material.id);
  const testIds = config.tests.map(test => test.id);
  const evidenceIds = config.tests.flatMap(test =>
    Object.values(test.observations).map(observation => observation.evidenceId));
  const knownKeys = new Set(config.tests.flatMap(test =>
    materialIds.map(materialId => materialTestKey(materialId, test.id))));

  if (!config.id || !config.masteryKey || !config.task.purpose || !config.conclusionId) {
    throw new Error(`Invalid materials investigation identity: ${config.id}`);
  }
  if (config.materials.length < 2 || config.tests.length < 1) {
    throw new Error(`Materials investigation ${config.id} needs materials and tests.`);
  }
  if (
    findDuplicates(materialIds).length > 0
    || findDuplicates(testIds).length > 0
    || findDuplicates(evidenceIds).length > 0
    || findDuplicates(config.task.requiredObservationKeys).length > 0
  ) {
    throw new Error(`Duplicate materials investigation identifiers in ${config.id}.`);
  }
  if (!materialIds.includes(config.bestMaterialId)) {
    throw new Error(`Unknown best material in ${config.id}: ${config.bestMaterialId}`);
  }
  if (
    config.minimumMaterialsInspected < 1
    || config.minimumMaterialsInspected > config.materials.length
  ) {
    throw new Error(`Invalid inspection requirement in ${config.id}.`);
  }
  for (const material of config.materials) {
    if (!material.label || findDuplicates(material.properties).length > 0) {
      throw new Error(`Invalid material definition in ${config.id}: ${material.id}`);
    }
  }
  for (const test of config.tests) {
    if (!test.label || test.propertyOptions.length < 2) {
      throw new Error(`Invalid property test in ${config.id}: ${test.id}`);
    }
    for (const materialId of materialIds) {
      const observation = test.observations[materialId];
      if (!observation || !test.propertyOptions.includes(observation.propertyId)) {
        throw new Error(`Missing or invalid ${test.id} result for ${materialId}.`);
      }
      const material = config.materials.find(candidate => candidate.id === materialId)!;
      if (!material.properties.includes(observation.propertyId)) {
        throw new Error(`${test.id} result contradicts ${materialId} properties.`);
      }
    }
  }
  if (config.task.requiredObservationKeys.some(key => !knownKeys.has(key))) {
    throw new Error(`Unknown required observation in ${config.id}.`);
  }
  const bestMaterial = config.materials.find(material => material.id === config.bestMaterialId)!;
  if (config.task.requiredProperties.some(property => !bestMaterial.properties.includes(property))) {
    throw new Error(`Best material does not satisfy the task in ${config.id}.`);
  }
  for (const property of config.task.requiredProperties) {
    const hasRequiredEvidence = config.tests.some(test => {
      const key = materialTestKey(config.bestMaterialId, test.id);
      return config.task.requiredObservationKeys.includes(key)
        && test.observations[config.bestMaterialId]?.propertyId === property;
    });
    if (!hasRequiredEvidence) {
      throw new Error(`Required property ${property} lacks evidence in ${config.id}.`);
    }
  }
  if (config.choiceFeedback.some(feedback => !materialIds.includes(feedback.materialId))) {
    throw new Error(`Unknown choice feedback material in ${config.id}.`);
  }
  return config;
}
